import React, { useState, useRef, useEffect } from "react";
import "./App.css";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { ChevronDown, ArrowRight } from "lucide-react";
import { WaveCanvas, SiteNav, SiteFooter } from "./Home";

function AccordionItem({ num, title, body, isOpen, onClick }) {
  const bodyRef = useRef(null);
  const innerRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!innerRef.current) return;

    const observer = new ResizeObserver(() => {
      if (innerRef.current) {
        setHeight(innerRef.current.scrollHeight + 40);
      }
    });

    observer.observe(innerRef.current);
    return () => observer.disconnect();
  }, [body]);

  return (
    <div className={`ow-acc-item${isOpen ? " open" : ""}`} onClick={onClick}>
      <div className="ow-acc-header">
        <span className="ow-acc-num">{num}</span>
        <span className="ow-acc-title">{title}</span>
        <ChevronDown
          size={18}
          strokeWidth={2}
          className={`ow-acc-chevron${isOpen ? " open" : ""}`}
        />
      </div>
      <div
        ref={bodyRef}
        className="ow-acc-body"
        style={{
          maxHeight: isOpen ? `${height}px` : "0px",
          overflow: "hidden",
          transition: "max-height 0.38s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <p ref={innerRef}>{body}</p>
      </div>
    </div>
  );
}

export default function Services() {
  const [active, setActive] = useState(null);
  const { t } = useTranslation();

  const items = [1, 2, 3, 4, 5].map((n) => ({
    title: t(`service-titre-${n}`),
    body: t(`service-text-${n}`),
  }));

  return (
    <>
      <Helmet>
        <title>Olympic Wave | Services</title>
        <meta
          name="description"
          content="Wajdi Bouallègue offers high-performance gymnastics coaching, athlete development, and professional guidance for competitive gymnasts."
        />
        <meta
          name="keywords"
          content="Wajdi Bouallègue, gymnastics coach, high performance gymnastics coaching, athlete development, elite gymnastics training"
        />
        <meta
          property="og:title"
          content="Gymnastics Coaching & Athlete Development | Wajdi Bouallègue"
        />
        <meta
          property="og:description"
          content="Discover professional gymnastics coaching by Wajdi Bouallègue."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://olympicwave.ca/services" />
      </Helmet>

      <WaveCanvas />
      <div className="ow-page">
        <SiteNav />

        <main className="ow-inner" style={{ maxWidth: "1200px" }}>
          <div className="ow-page-header">
            <p className="ow-eyebrow">{t("highPerf")}</p>
            <h1 className="ow-page-title">{t("messervices")}</h1>
            <p className="ow-page-lead">{t("unlock")}</p>
            <p className="ow-page-body">{t("with")}</p>
          </div>

          <div className="ow-accordion">
            {items.map(({ title, body }, i) => (
              <AccordionItem
                key={i}
                num={`0${i + 1}`}
                title={title}
                body={body}
                isOpen={active === i}
                onClick={() => setActive(active === i ? null : i)}
              />
            ))}
          </div>

          <div className="ow-cta-box">
            <p className="ow-cta-box-title">{t("build")}</p>
            <p className="ow-cta-box-sub">{t("if")}</p>
            <p className="ow-cta-box-book">
              <strong>{t("book")}</strong> {t("or")}
            </p>
            <Link
              to="/contact"
              className="ow-btn ow-btn-primary"
              style={{ marginTop: "1.25rem", display: "inline-flex" }}
            >
              {t("getintouch")} <ArrowRight size={15} strokeWidth={2.5} />
            </Link>
          </div>
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
